"use client"

import type { FC, SVGProps } from "react"
import * as React from "react"
import * as ContextMenuPrimitive from "@radix-ui/react-context-menu"
import {
	IconCheckOutline18 as Check,
	IconChevronRightOutline18 as ChevronRight,
	IconCircleDashedOutline18 as Circle,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/browser"

import type { IconType } from "../icons"

type MenuIcon = FC<SVGProps<SVGSVGElement>> | IconType

// Same surface, rows and spacing as DropdownMenu, so a right-click menu reads as the same control.
const SURFACE =
	"z-50 min-w-[8rem] overflow-hidden rounded-lg bg-white p-1 text-primary shadow-lg ring-1 ring-black/15 !duration-150 ease-out-cubic data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 dark:bg-gray-850 dark:shadow-black dark:ring-white/15"

const ROW =
	"relative flex cursor-default select-none items-center rounded-md px-2 py-1.5 text-sm font-medium outline-none focus:bg-gray-100 data-[disabled]:pointer-events-none data-[disabled]:opacity-50 dark:focus:bg-gray-800"

const ContextMenu = ContextMenuPrimitive.Root

const ContextMenuTrigger = ContextMenuPrimitive.Trigger

const ContextMenuGroup = ContextMenuPrimitive.Group

const ContextMenuPortal = ContextMenuPrimitive.Portal

const ContextMenuSub = ContextMenuPrimitive.Sub

const ContextMenuRadioGroup = ContextMenuPrimitive.RadioGroup

const ContextMenuShortcut = ({
	className,
	...props
}: React.HTMLAttributes<HTMLSpanElement>) => {
	return (
		<span
			className={cn(
				"ml-auto flex-none pl-4 text-xs tracking-widest text-gray-500",
				className,
			)}
			{...props}
		/>
	)
}
ContextMenuShortcut.displayName = "ContextMenuShortcut"

const ContextMenuSubTrigger = React.forwardRef<
	React.ElementRef<typeof ContextMenuPrimitive.SubTrigger>,
	React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.SubTrigger> & {
		inset?: boolean
		Icon?: MenuIcon
		shortcut?: React.ReactNode
	}
>(({ className, inset, Icon, shortcut, children, ...props }, ref) => (
	<ContextMenuPrimitive.SubTrigger
		ref={ref}
		className={cn(
			ROW,
			"data-[state=open]:bg-gray-100 dark:data-[state=open]:bg-gray-800",
			inset && "pl-8",
			className,
		)}
		{...props}
	>
		{Icon ? (
			<Icon
				strokeWidth={1.75}
				className="mr-2 h-4 w-4 flex-none text-secondary"
			/>
		) : null}
		{children}
		{shortcut ? <ContextMenuShortcut>{shortcut}</ContextMenuShortcut> : null}
		<ChevronRight
			className={cn(
				"h-3 w-3 flex-none text-secondary",
				shortcut ? "ml-2" : "ml-auto",
			)}
		/>
	</ContextMenuPrimitive.SubTrigger>
))

const ContextMenuSubContent = React.forwardRef<
	React.ElementRef<typeof ContextMenuPrimitive.SubContent>,
	React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.SubContent>
>(({ className, ...props }, ref) => (
	<ContextMenuPrimitive.Portal>
		<ContextMenuPrimitive.SubContent
			ref={ref}
			className={cn(SURFACE, className)}
			{...props}
		/>
	</ContextMenuPrimitive.Portal>
))

const ContextMenuContent = React.forwardRef<
	React.ElementRef<typeof ContextMenuPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.Content>
>(({ className, ...props }, ref) => (
	<ContextMenuPrimitive.Portal>
		<ContextMenuPrimitive.Content
			ref={ref}
			className={cn(SURFACE, className)}
			{...props}
		/>
	</ContextMenuPrimitive.Portal>
))

const ContextMenuItem = React.forwardRef<
	React.ElementRef<typeof ContextMenuPrimitive.Item>,
	React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.Item> & {
		inset?: boolean
		Icon?: MenuIcon
		iconClassName?: string
		shortcut?: React.ReactNode
	}
>(
	(
		{ className, inset, Icon, iconClassName, shortcut, children, ...props },
		ref,
	) => (
		<ContextMenuPrimitive.Item
			ref={ref}
			className={cn(ROW, inset && "pl-8", className)}
			{...props}
		>
			{Icon ? (
				<Icon
					strokeWidth={1.75}
					className={cn("mr-2 h-4 w-4 flex-none text-secondary", iconClassName)}
				/>
			) : null}
			{children}
			{shortcut ? <ContextMenuShortcut>{shortcut}</ContextMenuShortcut> : null}
		</ContextMenuPrimitive.Item>
	),
)

const ContextMenuCheckboxItem = React.forwardRef<
	React.ElementRef<typeof ContextMenuPrimitive.CheckboxItem>,
	React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.CheckboxItem>
>(({ className, children, checked, ...props }, ref) => (
	<ContextMenuPrimitive.CheckboxItem
		ref={ref}
		className={cn(ROW, "pl-8", className)}
		checked={checked}
		{...props}
	>
		<span className="absolute left-2 flex h-4 w-4 items-center justify-center">
			<ContextMenuPrimitive.ItemIndicator>
				<Check className="h-4 w-4 flex-none" />
			</ContextMenuPrimitive.ItemIndicator>
		</span>
		{children}
	</ContextMenuPrimitive.CheckboxItem>
))

const ContextMenuRadioItem = React.forwardRef<
	React.ElementRef<typeof ContextMenuPrimitive.RadioItem>,
	React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.RadioItem>
>(({ className, children, ...props }, ref) => (
	<ContextMenuPrimitive.RadioItem
		ref={ref}
		className={cn(ROW, "pl-8", className)}
		{...props}
	>
		<span className="absolute left-2 flex h-4 w-4 items-center justify-center">
			<ContextMenuPrimitive.ItemIndicator>
				<Circle className="h-2 w-2 flex-none fill-current" />
			</ContextMenuPrimitive.ItemIndicator>
		</span>
		{children}
	</ContextMenuPrimitive.RadioItem>
))

const ContextMenuLabel = React.forwardRef<
	React.ElementRef<typeof ContextMenuPrimitive.Label>,
	React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.Label> & {
		inset?: boolean
	}
>(({ className, inset, ...props }, ref) => (
	<ContextMenuPrimitive.Label
		ref={ref}
		className={cn(
			"px-2 py-1.5 text-xs font-medium text-secondary",
			inset && "pl-8",
			className,
		)}
		{...props}
	/>
))

const ContextMenuSeparator = React.forwardRef<
	React.ElementRef<typeof ContextMenuPrimitive.Separator>,
	React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
	<ContextMenuPrimitive.Separator
		ref={ref}
		className={cn("-mx-1 my-1 h-px bg-gray-100 dark:bg-gray-800", className)}
		{...props}
	/>
))

export {
	ContextMenu,
	ContextMenuTrigger,
	ContextMenuContent,
	ContextMenuItem,
	ContextMenuCheckboxItem,
	ContextMenuRadioItem,
	ContextMenuLabel,
	ContextMenuSeparator,
	ContextMenuShortcut,
	ContextMenuGroup,
	ContextMenuPortal,
	ContextMenuSub,
	ContextMenuSubContent,
	ContextMenuSubTrigger,
	ContextMenuRadioGroup,
}
